import React, { useState, useEffect } from 'react';
import { Palette, Upload, Save, RefreshCw, Eye, CheckCircle } from 'lucide-react';

import { useAuth } from '../../../contexts/AuthContext';
import { tenantService } from '../../../services/tenantService';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

export default function BrandingControlsSection() {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [branding, setBranding] = useState({
    logoUrl: '',
    primaryColor: '#3B82F6',
    secondaryColor: '#8B5CF6',
    organizationName: ''
  });
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    loadTenantBranding();
  }, []);

  const loadTenantBranding = async () => {
    try {
      setLoading(true);
      const data = await tenantService?.getTenantById(profile?.tenant_id);
      
      if (data) {
        setTenant(data);
        setBranding({
          logoUrl: data?.logo_url || '',
          primaryColor: data?.primary_color || '#3B82F6',
          secondaryColor: data?.secondary_color || '#8B5CF6',
          organizationName: data?.name || ''
        });
      }
    } catch (error) {
      console.error('Error loading tenant branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBranding = async () => {
    try {
      setSaving(true);
      
      await tenantService?.updateTenant(profile?.tenant_id, {
        name: branding?.organizationName,
        logo_url: branding?.logoUrl,
        primary_color: branding?.primaryColor,
        secondary_color: branding?.secondaryColor
      });

      alert('Branding settings saved successfully!');
      await loadTenantBranding();
    } catch (error) {
      console.error('Error saving branding:', error);
      alert('Failed to save branding settings: ' + error?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = () => {
    // Mock logo upload - in production, integrate with Supabase Storage
    const mockLogoUrl = 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=200&h=200&fit=crop';
    setBranding(prev => ({ ...prev, logoUrl: mockLogoUrl }));
  };

  const handleReset = () => {
    if (confirm('Are you sure you want to reset to default branding?')) {
      setBranding({
        logoUrl: '',
        primaryColor: '#3B82F6',
        secondaryColor: '#8B5CF6',
        organizationName: tenant?.name || ''
      });
    }
  };

  const colorPresets = [
    { name: 'Blue', primary: '#3B82F6', secondary: '#8B5CF6' },
    { name: 'Green', primary: '#10B981', secondary: '#059669' },
    { name: 'Purple', primary: '#8B5CF6', secondary: '#7C3AED' },
    { name: 'Red', primary: '#EF4444', secondary: '#DC2626' },
    { name: 'Orange', primary: '#F97316', secondary: '#EA580C' },
    { name: 'Pink', primary: '#EC4899', secondary: '#DB2777' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Branding Controls</h2>
          <p className="text-sm text-gray-600 mt-1">
            Customize your organization's logo, colors, and theme
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setPreviewMode(!previewMode)}
          >
            <Eye className="w-4 h-4 mr-2" />
            {previewMode ? 'Exit Preview' : 'Preview'}
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSaveBranding}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="ml-2 text-gray-500">Loading branding settings...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Settings Panel */}
          <div className="space-y-6">
            {/* Organization Name */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Organization Name</h3>
              <Input
                type="text"
                value={branding?.organizationName}
                onChange={(e) => setBranding(prev => ({ ...prev, organizationName: e?.target?.value }))}
                placeholder="Enter organization name"
              />
            </div>

            {/* Logo Upload */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Organization Logo</h3>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                  {branding?.logoUrl ? (
                    <img
                      src={branding?.logoUrl}
                      alt="Organization logo preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Upload className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-2">
                    Upload your organization logo. Recommended size: 200x200px
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLogoUpload}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Logo
                    </Button>
                    {branding?.logoUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setBranding(prev => ({ ...prev, logoUrl: '' }))}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Color Picker */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Brand Colors</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={branding?.primaryColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, primaryColor: e?.target?.value }))}
                      className="w-12 h-12 rounded border border-gray-300 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={branding?.primaryColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, primaryColor: e?.target?.value }))}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secondary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={branding?.secondaryColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, secondaryColor: e?.target?.value }))}
                      className="w-12 h-12 rounded border border-gray-300 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={branding?.secondaryColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, secondaryColor: e?.target?.value }))}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {/* Color Presets */}
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Quick Presets</p>
                <div className="grid grid-cols-3 gap-2">
                  {colorPresets?.map((preset) => (
                    <button
                      key={preset?.name}
                      onClick={() => setBranding(prev => ({
                        ...prev,
                        primaryColor: preset?.primary,
                        secondaryColor: preset?.secondary
                      }))}
                      className="flex items-center gap-2 p-2 rounded border border-gray-200 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex gap-1">
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: preset?.primary }}
                        />
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: preset?.secondary }}
                        />
                      </div>
                      <span className="text-xs text-gray-700">{preset?.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Live Preview Panel */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Preview</h3>
            
            <div className="space-y-4">
              {/* Header Preview */}
              <div
                className="rounded-lg p-4"
                style={{ backgroundColor: branding?.primaryColor }}
              >
                <div className="flex items-center gap-3">
                  {branding?.logoUrl ? (
                    <img
                      src={branding?.logoUrl}
                      alt="Logo preview"
                      className="w-10 h-10 rounded"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-white/20 rounded flex items-center justify-center">
                      <Palette className="w-6 h-6 text-white" />
                    </div>
                  )}
                  <span className="text-white font-semibold text-lg">
                    {branding?.organizationName || 'Your Organization'}
                  </span>
                </div>
              </div>

              {/* Button Preview */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Button Styles</p>
                <div className="flex gap-2">
                  <button
                    className="px-4 py-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: branding?.primaryColor }}
                  >
                    Primary Button
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: branding?.secondaryColor }}
                  >
                    Secondary Button
                  </button>
                </div>
              </div>

              {/* Card Preview */}
              <div className="border-2 rounded-lg p-4" style={{ borderColor: branding?.primaryColor }}>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5" style={{ color: branding?.primaryColor }} />
                  <h4 className="font-semibold" style={{ color: branding?.primaryColor }}>
                    Sample Card
                  </h4>
                </div>
                <p className="text-sm text-gray-600">
                  This is how your branded components will appear throughout the application.
                </p>
              </div>

              {/* Badge Preview */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Badge Styles</p>
                <div className="flex gap-2">
                  <span
                    className="px-3 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: branding?.primaryColor }}
                  >
                    Active
                  </span>
                  <span
                    className="px-3 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: branding?.secondaryColor }}
                  >
                    Featured
                  </span>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Changes will be applied across all pages and components after saving.
                  Users may need to refresh their browser to see the updates.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
